import { computed, nextTick } from 'vue';

import store from '@/study-search/store';
import { ParameterType } from '@/study-search/enums';
import { AdTargetingOptions, BannerTypes, IDefinedSlot } from '@/study-search/models/banner/IGoogleTag';
import GoogleAdMethodsHandler from '@/study-search/utils/GoogleAdMethodsHandler';

export default class Banner {
	public selectedDiscipline = computed((): string => {
		return store.getters.getFilterValues(ParameterType.DISCIPLINES)[0];
	});

	private readonly containerId: string;
	private readonly type: BannerTypes;
	private readonly cssClass: string;

	constructor(containerId: string, type: BannerTypes, cssClass: string) {
		this.containerId = containerId;
		this.type = type;
		this.cssClass = cssClass;
	}

	public updateBanners(): void {
		this.handleBannerLoading();
	}

	public async mounted(): Promise<void> {
		if (this.isApplicationRunningStandAlone()) {
			return;
		}

		await this.checkWhetherBannerCanBeLoaded();
	}

	private async checkWhetherBannerCanBeLoaded(): Promise<void> {
		if (!this.hasGoogleAdManagerFinishedLoading()) {
			document.addEventListener('gam_ready', () => void this.checkWhetherBannerCanBeLoaded());
			return;
		}

		this.displayBannerSlot();

		await nextTick(this.handleBannerLoading.bind(this));
	}

	private displayBannerSlot(): void {
		GoogleAdMethodsHandler.cmdPush(() => GoogleAdMethodsHandler.displayContainer(this.containerId));
	}

	private handleBannerLoading(): void {
		if (!window.adSlots || !Object.prototype.hasOwnProperty.call(window.adSlots, this.type)) {
			return;
		}

		this.onRightBannerProceedWithLoading(window.adSlots[this.type]);
	}

	private onRightBannerProceedWithLoading(listOfSlots: IDefinedSlot[]): void {
		const isLastSlotInList =
			listOfSlots.find(
				(slot, index) => slot.getSlotElementId() === this.containerId && index === listOfSlots.length - 1
			) !== undefined;
		if (!isLastSlotInList) {
			return;
		}

		this.clearBannerTargetingIfSet();
		this.useSelectedDisciplineForBannerTargeting();

		GoogleAdMethodsHandler.callPubadsRefreshMethod(listOfSlots);
	}

	private useSelectedDisciplineForBannerTargeting(): void {
		if (!this.selectedDiscipline.value) {
			return;
		}

		const targetedDiscipline = this.getTargetedDiscipline();
		if (targetedDiscipline && targetedDiscipline === this.selectedDiscipline.value) {
			return;
		}

		GoogleAdMethodsHandler.callPubadsSetTargetingMethod(AdTargetingOptions.DISCIPLINE, this.selectedDiscipline.value);
	}

	private clearBannerTargetingIfSet(): void {
		const targetedDiscipline = this.getTargetedDiscipline();
		if (!targetedDiscipline) {
			return;
		}

		GoogleAdMethodsHandler.callPubadsClearTargetingMethod(AdTargetingOptions.DISCIPLINE);
	}

	private isApplicationRunningStandAlone(): boolean {
		return window.googletag === undefined;
	}

	private hasGoogleAdManagerFinishedLoading(): boolean {
		return window.googletag.pubadsReady;
	}

	private getTargetedDiscipline(): string | false {
		const currentTargeting = GoogleAdMethodsHandler.callPubadsGetTargetingMethod(AdTargetingOptions.DISCIPLINE);
		if (!currentTargeting?.length) {
			return false;
		}

		return currentTargeting[0];
	}
}
